import { z } from 'zod';

import {
  materialCaptureUnitValues,
  materialPresentationTypeValues,
} from '../materials-catalog/index.js';
import type {
  ClearMaterialCategoryConfigRequest,
  ClearMaterialCategoryConfigResponse,
  ClearMaterialOrderingConfigRequest,
  ClearMaterialOrderingConfigResponse,
  GetMaterialCategoryConfigRequest,
  GetMaterialCategoryConfigResponse,
  GetMaterialOrderingConfigRequest,
  GetMaterialOrderingConfigResponse,
  MaterialCardRef,
  MaterialCategoryConfigDocument,
  MaterialCategoryConfigEntry,
  MaterialCategoryRule,
  MaterialCategoryRuleType,
  MaterialOrderingBuckets,
  MaterialOrderingConfigDocument,
  MaterialOrderingHiddenRef,
  MaterialOrderingRestoreBucket,
  MaterialsOrderingDefaultMode,
  MaterialsPresentationSource,
  ReplaceMaterialCategoryConfigRequest,
  ReplaceMaterialCategoryConfigResponse,
  ReplaceMaterialOrderingConfigRequest,
  ReplaceMaterialOrderingConfigResponse,
  ResolveMaterialPresentationRequest,
  ResolveMaterialPresentationResponse,
  ResolvedMaterialPresentationCard,
  ResolvedMaterialPresentationGroup,
  ResolvedMaterialPresentationSourceInfo,
  ResolvedMaterialPresentationWarning,
} from './materials-presentation.dto.js';
import {
  materialCategoryRuleTypeValues,
  materialOrderingRestoreBucketValues,
  materialsOrderingDefaultModeValues,
  materialsPresentationSourceValues,
} from './materials-presentation.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const MaterialPresentationTypeSchema = z.enum(materialPresentationTypeValues);
const MaterialCaptureUnitSchema = z.enum(materialCaptureUnitValues);

export const MaterialsPresentationSourceSchema: z.ZodType<MaterialsPresentationSource> = z.enum(
  materialsPresentationSourceValues,
);
export const MaterialsOrderingDefaultModeSchema: z.ZodType<MaterialsOrderingDefaultMode> = z.enum(
  materialsOrderingDefaultModeValues,
);
export const MaterialOrderingRestoreBucketSchema: z.ZodType<MaterialOrderingRestoreBucket> = z.enum(
  materialOrderingRestoreBucketValues,
);
export const MaterialCategoryRuleTypeSchema: z.ZodType<MaterialCategoryRuleType> = z.enum(
  materialCategoryRuleTypeValues,
);

export const MaterialCardRefSchema: z.ZodType<MaterialCardRef> = z
  .object({
    materialId: z.uuid(),
    presentationType: MaterialPresentationTypeSchema.nullable().optional(),
  })
  .strict();

export const MaterialOrderingHiddenRefSchema: z.ZodType<MaterialOrderingHiddenRef> = z
  .object({
    ref: MaterialCardRefSchema,
    restoreBucket: MaterialOrderingRestoreBucketSchema,
  })
  .strict();

const pinnedOrVisibleRefsSchema = z
  .array(MaterialCardRefSchema)
  .superRefine((items, ctx) => {
    validateUniqueRefs(items, ctx);
  });

export const MaterialOrderingBucketsSchema: z.ZodType<MaterialOrderingBuckets> = z
  .object({
    pinned: pinnedOrVisibleRefsSchema,
    visible: pinnedOrVisibleRefsSchema,
    hidden: z
      .array(MaterialOrderingHiddenRefSchema)
      .superRefine((items, ctx) => {
        validateUniqueRefs(items.map((item) => item.ref), ctx);
      }),
  })
  .strict()
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const ref of value.pinned) {
      const key = toCardRefKey(ref);
      if (seen.has(key)) {
        addDuplicateRefIssue(ctx, ref);
        return;
      }
      seen.add(key);
    }
    for (const ref of value.visible) {
      const key = toCardRefKey(ref);
      if (seen.has(key)) {
        addDuplicateRefIssue(ctx, ref);
        return;
      }
      seen.add(key);
    }
    for (const item of value.hidden) {
      const key = toCardRefKey(item.ref);
      if (seen.has(key)) {
        addDuplicateRefIssue(ctx, item.ref);
        return;
      }
      seen.add(key);
    }
  });

const PresentationRuleSchema = z
  .object({
    type: z.literal('presentationTypes'),
    presentationTypes: z.array(MaterialPresentationTypeSchema).nonempty(),
  })
  .strict();
const UnitRuleSchema = z
  .object({
    type: z.literal('unit'),
    unit: MaterialCaptureUnitSchema,
  })
  .strict();
const CrvEligibleRuleSchema = z
  .object({
    type: z.literal('crvEligible'),
    isCrvEligible: z.boolean(),
  })
  .strict();

export const MaterialCategoryRuleSchema: z.ZodType<MaterialCategoryRule> = z.union([
  PresentationRuleSchema,
  UnitRuleSchema,
  CrvEligibleRuleSchema,
]);

export const MaterialCategoryConfigEntrySchema: z.ZodType<MaterialCategoryConfigEntry> = z
  .object({
    id: z.uuid(),
    name: z.string().trim().min(1).max(200),
    isActive: z.boolean(),
    rule: MaterialCategoryRuleSchema.nullable(),
    members: z.array(MaterialCardRefSchema).superRefine((items, ctx) => {
      validateUniqueRefs(items, ctx);
    }),
  })
  .strict();

export const MaterialOrderingConfigDocumentSchema: z.ZodType<MaterialOrderingConfigDocument> = z
  .object({
    version: z.literal(1),
    companyId: z.uuid(),
    stationId: z.uuid().nullable(),
    revision: z.number().int().min(0),
    updatedAt: timestampSchema,
    updatedByUserId: z.uuid().nullable(),
    defaultMode: MaterialsOrderingDefaultModeSchema,
    buckets: MaterialOrderingBucketsSchema,
  })
  .strict();

export const MaterialCategoryConfigDocumentSchema: z.ZodType<MaterialCategoryConfigDocument> = z
  .object({
    version: z.literal(1),
    companyId: z.uuid(),
    stationId: z.uuid().nullable(),
    revision: z.number().int().min(0),
    updatedAt: timestampSchema,
    updatedByUserId: z.uuid().nullable(),
    categories: z.array(MaterialCategoryConfigEntrySchema),
  })
  .strict();

const ReplaceMaterialOrderingConfigDocumentInputSchema = z
  .object({
    version: z.literal(1),
    defaultMode: MaterialsOrderingDefaultModeSchema,
    buckets: MaterialOrderingBucketsSchema,
  })
  .strict();

const ReplaceMaterialCategoryConfigDocumentInputSchema = z
  .object({
    version: z.literal(1),
    categories: z.array(MaterialCategoryConfigEntrySchema),
  })
  .strict();

export const GetMaterialOrderingConfigRequestSchema: z.ZodType<GetMaterialOrderingConfigRequest> = z
  .object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
  })
  .strict();

export const GetMaterialOrderingConfigResponseSchema: z.ZodType<GetMaterialOrderingConfigResponse> = z
  .object({
    ordering: MaterialOrderingConfigDocumentSchema.nullable(),
  })
  .strict();

export const ReplaceMaterialOrderingConfigRequestSchema: z.ZodType<ReplaceMaterialOrderingConfigRequest> = z
  .object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
    expectedRevision: z.number().int().min(0).nullable().optional(),
    document: ReplaceMaterialOrderingConfigDocumentInputSchema,
  })
  .strict();

export const ReplaceMaterialOrderingConfigResponseSchema: z.ZodType<ReplaceMaterialOrderingConfigResponse> = z
  .object({
    ordering: MaterialOrderingConfigDocumentSchema,
  })
  .strict();

export const ClearMaterialOrderingConfigRequestSchema: z.ZodType<ClearMaterialOrderingConfigRequest> = z
  .object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
    expectedRevision: z.number().int().min(0).nullable().optional(),
  })
  .strict();

export const ClearMaterialOrderingConfigResponseSchema: z.ZodType<ClearMaterialOrderingConfigResponse> = z
  .object({
    ordering: z.null(),
  })
  .strict();

export const GetMaterialCategoryConfigRequestSchema: z.ZodType<GetMaterialCategoryConfigRequest> = z
  .object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
  })
  .strict();

export const GetMaterialCategoryConfigResponseSchema: z.ZodType<GetMaterialCategoryConfigResponse> = z
  .object({
    categories: MaterialCategoryConfigDocumentSchema.nullable(),
  })
  .strict();

export const ReplaceMaterialCategoryConfigRequestSchema: z.ZodType<ReplaceMaterialCategoryConfigRequest> = z
  .object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
    expectedRevision: z.number().int().min(0).nullable().optional(),
    document: ReplaceMaterialCategoryConfigDocumentInputSchema,
  })
  .strict();

export const ReplaceMaterialCategoryConfigResponseSchema: z.ZodType<ReplaceMaterialCategoryConfigResponse> = z
  .object({
    categories: MaterialCategoryConfigDocumentSchema,
  })
  .strict();

export const ClearMaterialCategoryConfigRequestSchema: z.ZodType<ClearMaterialCategoryConfigRequest> = z
  .object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
    expectedRevision: z.number().int().min(0).nullable().optional(),
  })
  .strict();

export const ClearMaterialCategoryConfigResponseSchema: z.ZodType<ClearMaterialCategoryConfigResponse> = z
  .object({
    categories: z.null(),
  })
  .strict();

export const ResolveMaterialPresentationRequestSchema: z.ZodType<ResolveMaterialPresentationRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
    effectiveOn: dateSchema.optional(),
    includeInactive: z.boolean().optional(),
  })
  .strict();

const RequiredMaterialCardRefSchema = z
  .object({
    materialId: z.uuid(),
    presentationType: MaterialPresentationTypeSchema,
  })
  .strict();

export const ResolvedMaterialPresentationCardSchema: z.ZodType<ResolvedMaterialPresentationCard> = z
  .object({
    ref: RequiredMaterialCardRefSchema,
    displayName: z.string().trim().min(1),
    presentationLabel: z.string().trim().min(1),
    categoryName: z.string().trim().min(1).nullable(),
    source: MaterialsPresentationSourceSchema,
  })
  .strict();

export const ResolvedMaterialPresentationGroupSchema: z.ZodType<ResolvedMaterialPresentationGroup> = z
  .object({
    id: z.string().trim().min(1),
    name: z.string().trim().min(1),
    cards: z.array(ResolvedMaterialPresentationCardSchema),
  })
  .strict();

export const ResolvedMaterialPresentationSourceInfoSchema: z.ZodType<ResolvedMaterialPresentationSourceInfo> = z
  .object({
    source: MaterialsPresentationSourceSchema,
    revision: z.number().int().min(0).nullable(),
  })
  .strict();

export const ResolvedMaterialPresentationWarningSchema: z.ZodType<ResolvedMaterialPresentationWarning> = z
  .object({
    code: z.literal('legacy_category_overlap'),
    message: z.string().trim().min(1),
    details: z.unknown().optional(),
  })
  .strict();

export const ResolveMaterialPresentationResponseSchema: z.ZodType<ResolveMaterialPresentationResponse> = z
  .object({
    pinned: z.array(ResolvedMaterialPresentationCardSchema),
    groups: z.array(ResolvedMaterialPresentationGroupSchema),
    otherMaterials: z.array(ResolvedMaterialPresentationCardSchema),
    hiddenCount: z.number().int().min(0),
    orderingSource: ResolvedMaterialPresentationSourceInfoSchema,
    categorySource: ResolvedMaterialPresentationSourceInfoSchema,
    warnings: z.array(ResolvedMaterialPresentationWarningSchema),
  })
  .strict();

function validateUniqueRefs(
  items: MaterialCardRef[],
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  for (const item of items) {
    const key = toCardRefKey(item);
    if (seen.has(key)) {
      addDuplicateRefIssue(ctx, item);
      return;
    }
    seen.add(key);
  }
}

function toCardRefKey(value: MaterialCardRef): string {
  return `${value.materialId}:${value.presentationType ?? '*'}`;
}

function addDuplicateRefIssue(ctx: z.RefinementCtx, ref: MaterialCardRef): void {
  ctx.addIssue({
    code: 'custom',
    message: `Duplicate material card reference: ${toCardRefKey(ref)}`,
  });
}
